import { apiClient } from './apiClient';

// Define the structure of custom claims
export interface CustomClaims {
  role?: 'fleetManager' | 'trader';
  admin?: boolean;
  fleetManager?: boolean;
  trader?: boolean;
  fleetPermissions?: Record<string, Record<string, boolean>>;
  traderPermissions?: Record<string, Record<string, boolean>>;
}

export interface UserInfo {
  uid: string;
  email: string;
  displayName: string;
  disabled: boolean;
  customClaims: CustomClaims;
  creationTime: string;
  lastSignIn: string;
}

// PermissionsRequest extends CustomClaims but requires role to be specified
export interface PermissionsRequest
  extends Required<Pick<CustomClaims, 'role'>>,
    Omit<CustomClaims, 'role'> {}

export interface CreateUserRequest {
  email: string;
  password: string;
  displayName?: string;
  role: 'fleetManager' | 'trader' | 'custom';
  admin?: boolean;
  fleetManager?: boolean;
  trader?: boolean;
  fleetPermissions?: Record<string, Record<string, boolean>>;
  traderPermissions?: Record<string, Record<string, boolean>>;
}

export interface UsersResponse {
  users: UserInfo[];
  total: number;
}

export interface PermissionUpdateResponse {
  message: string;
  userID: string;
  role: string;
}

export interface CreateUserResponse {
  message: string;
  user: UserInfo;
}

export class UserService {
  /**
   * Get all users with their permissions
   */
  static async getAllUsers(): Promise<UsersResponse> {
    try {
      const response = await apiClient.get<UsersResponse>('/users');
      return response || { users: [], total: 0 };
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Return empty response structure to avoid undefined
      return { users: [], total: 0 };
    }
  }

  /**
   * Get a specific user by ID
   */
  static async getUser(userID: string): Promise<UserInfo> {
    try {
      const response = await apiClient.get<UserInfo>(`/users/${userID}`);
      if (!response) {
        throw new Error('User not found');
      }
      return response;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      throw error;
    }
  }

  /**
   * Create a new user with role/permissions
   */
  static async createUser(payload: CreateUserRequest): Promise<CreateUserResponse> {
    try {
      const response = await apiClient.post<CreateUserResponse>('/users', payload);
      if (!response) {
        throw new Error('Failed to create user');
      }
      return response;
    } catch (error) {
      console.error('Failed to create user:', error);
      throw error;
    }
  }

  /**
   * Update user permissions
   */
  static async updateUserPermissions(
    userID: string,
    permissions: PermissionsRequest
  ): Promise<PermissionUpdateResponse> {
    try {
      const response = await apiClient.put<PermissionUpdateResponse>(
        `/users/${userID}/permissions`,
        permissions
      );
      if (!response) {
        throw new Error('Failed to update permissions');
      }
      return response;
    } catch (error) {
      console.error('Failed to update user permissions:', error);
      throw error;
    }
  }

  /**
   * Revoke all user permissions
   */
  static async revokeUserPermissions(userID: string): Promise<PermissionUpdateResponse> {
    try {
      const response = await apiClient.delete<PermissionUpdateResponse>(
        `/users/${userID}/permissions`
      );
      if (!response) {
        throw new Error('Failed to revoke permissions');
      }
      return response;
    } catch (error) {
      console.error('Failed to revoke user permissions:', error);
      throw error;
    }
  }

  /**
   * Delete a user
   */
  static async deleteUser(userID: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.delete<{ message: string }>(`/users/${userID}`);
      if (!response) {
        throw new Error('Failed to delete user');
      }
      return response;
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  }

  /**
   * Generate standard fleet manager permissions
   */
  static generateFleetManagerPermissions(): PermissionsRequest {
    return {
      role: 'fleetManager',
      fleetManager: true,
      fleetPermissions: {
        contracts: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        invoices: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        vessels: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        voyages: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        charterers: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        procurements: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        pooling_contracts: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        notifications: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        users: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        entities: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        reports: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        fueleuCalculations: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
      },
    };
  }

  /**
   * Generate standard trader permissions
   */
  static generateTraderPermissions(): PermissionsRequest {
    return {
      role: 'trader',
      trader: true,
      traderPermissions: {
        contracts: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        invoices: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        vessels: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        voyages: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        charterers: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        procurements: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        pooling_contracts: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        notifications: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        users: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        entities: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        reports: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
        fueleuCalculations: {
          view: true,
          create: true,
          update: true,
          delete: true,
        },
      },
    };
  }

  /**
   * Get user role from custom claims
   */
  static getUserRole(user: UserInfo): string {
    if (!user.customClaims) return 'No Role';

    if (user.customClaims.role) {
      return user.customClaims.role;
    }

    // Check legacy claims
    if (user.customClaims.fleetManager) return 'fleetManager';
    if (user.customClaims.trader) return 'trader';

    return 'No Role';
  }

  /**
   * Check if user is an admin
   */
  static isAdmin(user: UserInfo): boolean {
    if (!user.customClaims) return false;
    return user.customClaims.admin === true;
  }

  /**
   * Check if user has structured permissions
   */
  static hasStructuredPermissions(user: UserInfo): boolean {
    if (!user.customClaims) return false;
    return !!(user.customClaims.fleetPermissions || user.customClaims.traderPermissions);
  }

  /**
   * Format user display name
   */
  static formatDisplayName(user: UserInfo): string {
    if (user.displayName) return user.displayName;
    if (user.email) return user.email.split('@')[0];
    return user.uid;
  }
}
